import { useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useEscapeLayer } from '../escStack';
import { useFocusTrap } from '../focusTrap';
import { IconButton } from './Button';
import s from './Drawer.module.css';

type Props = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  sub?: ReactNode;
  /** Extra header content under the sub-line (e.g. «Пусть AI решает всё»). */
  headerExtra?: ReactNode;
  /** Footer: a note on the left and actions on the right. */
  footerNote?: ReactNode;
  footer?: ReactNode;
  closeLabel: string;
  children: ReactNode;
};

/** Right drawer with a scrim: Esc (innermost layer) and scrim click close it; focus is trapped and restored. */
export function Drawer({ open, onClose, title, sub, headerExtra, footerNote, footer, closeLabel, children }: Props) {
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useEscapeLayer(open, onClose);
  useFocusTrap(panel, open);
  if (!open) return null;
  return createPortal(
    <>
      <div className={s.scrim} onClick={onClose} data-testid="drawer-scrim" />
      <div ref={panel} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} className={s.panel}>
        <div className={s.header}>
          <div className={s.titles}>
            <h2 id={titleId} className={s.title}>
              {title}
            </h2>
            {sub && <span className={s.sub}>{sub}</span>}
            {headerExtra}
          </div>
          <IconButton icon="x" label={closeLabel} tone="outline" size={36} onClick={onClose} />
        </div>
        <div className={s.body}>{children}</div>
        {(footer || footerNote) && (
          <div className={s.footer}>
            <span className={s.footerNote}>{footerNote}</span>
            {footer}
          </div>
        )}
      </div>
    </>,
    document.body,
  );
}
